"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { ServiceBlogFaq as FAQItem } from "@/types/serviceBlog.types";

interface ServiceBlogFaqProps {
  faqs: FAQItem[];
  /** Accent colour; defaults to the blog/treatment green. */
  primary?: string;
}

export default function ServiceBlogFaq({
  faqs,
  primary = "#008960",
}: ServiceBlogFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!faqs?.length) return null;

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div id="faq" className="w-full bg-white py-16 md:py-24 scroll-mt-20">
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <h2 className="text-[28px] md:text-[38px] font-medium text-gray-900 mb-2 tracking-tight">
          Frequently Asked Questions
        </h2>
      </div>

      {/* Accordion */}
      <div className="max-w-[820px] mx-auto flex flex-col gap-3">
        {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className="rounded-xl overflow-hidden bg-[#F8F7F5] border border-transparent transition-shadow duration-200"
              style={isOpen ? { borderColor: `${primary}22` } : {}}
            >
              <button
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
              >
                <span className="text-[15px] md:text-[16px] font-medium text-gray-900 pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className="w-5 h-5 shrink-0 transition-transform duration-300"
                  style={{
                    color: primary,
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>

              {/* Answer — animated open/close */}
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ maxHeight: isOpen ? "500px" : "0px" }}
              >
                <p className="px-6 pb-6 text-gray-500 text-[14px] md:text-[15px] leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}